import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Brand Colors ─────────────────────────────────────────────────────────────
const DARK_BROWN = [59, 36, 16];    // #3b2410
const GOLD       = [201, 168, 76];  // #c9a84c
const LIGHT_GOLD = [255, 255, 255]; // white
const BLACK      = [0, 0, 0];
const GREY_LIGHT = [245, 245, 245];
const GREY_MID   = [200, 200, 200];
const BLUE_LINK  = [0, 102, 204];

// ─── Build the jsPDF document and return it ───────────────────────────────────
function buildDoc(estimateData, companyData = {}) {
  const doc  = new jsPDF('l', 'mm', 'a4');
  const pw   = doc.internal.pageSize.getWidth();   // 210
  const ph   = doc.internal.pageSize.getHeight();  // 297
  const lm   = 6;
  const rm   = pw - lm;

  // Resolve company fields with fallbacks
  const companyName    = companyData.Name            || 'COCHIN FRAMES';
  const companyPhone1  = companyData.ContactNumber1  || '0484-4864027';
  const companyPhone2  = companyData.ContactNumber2  || '7356864227';
  const companyPhone3  = companyData.ContactNumber3  || '9037559716';
  const companyEmail   = companyData.Email           || 'cochinframes07@gmail.com';
  const companyWeb     = companyData.Website         || 'www.cochinframes.com';
  const companyAddr1   = companyData.AddressLine1    || 'Edappally -';
  const companyAddr2   = companyData.AddressLine2    || 'Tripunithura Road,';
  const companyCity    = companyData.Location        || 'Padivattom, Kochi-24.';

  // ══ TOP DECORATIVE BAR ══════════════════════════════════════════════════
  doc.setFillColor(245, 240, 230);
  doc.rect(0, 0, pw, 6, 'F');
  doc.setFillColor(245, 240, 230);
  doc.rect(0, 6, pw, 1.5, 'F');

  // ══ COMPANY NAME ════════════════════════════════════════════════════════
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK_BROWN);
  doc.text(companyName.toUpperCase(), pw / 2, 18, { align: 'center' });

  // Gold underline — width scales with name length
  const nameWidth = doc.getTextWidth(companyName.toUpperCase());
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(pw / 2 - nameWidth / 2 - 2, 20, pw / 2 + nameWidth / 2 + 2, 20);

  // Services tagline
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 70, 30);
  doc.text(
    'Photo Framing  |  Certificate Framing  |  Artwork Framing  |  Photo Restoration  |  Collage Framing',
    pw / 2, 25, { align: 'center' }
  );

  // ══ WORK ORDER BANNER ═══════════════════════════════════════════════════
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.4);
  doc.roundedRect(lm, 28, pw - lm * 2, 9, 2, 2, 'S');
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLACK);
  doc.text('WORK ORDER', pw / 2, 34.5, { align: 'center' });

  // ══ CUSTOMER INFO BOX ═══════════════════════════════════════════════════
  const boxY  = 40;
  const boxH  = 28;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.roundedRect(lm, boxY, pw - lm * 2, boxH, 2, 2, 'S');

  // Vertical dividers inside info box
  doc.setDrawColor(...GREY_MID);
  doc.setLineWidth(0.2);
  doc.line(lm + 140, boxY + 1, lm + 140, boxY + boxH - 1);
  doc.line(lm + 220, boxY + 1, lm + 220, boxY + boxH - 1);

  const infoY  = boxY + 6;
  const gap    = 6;

  // ── Left column ──
  const lc = lm + 3;
  const lv = lm + 24;
  doc.setFontSize(8.5);
  doc.setTextColor(...BLACK);

  infoRow(doc, lc, lv, infoY,        'Name',    estimateData.ClientName || 'ENQUIRY.');
  infoRow(doc, lc, lv, infoY + gap,  'Contact', estimateData.ContactNumber || '0');

  // Address — may wrap
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text('Address', lc, infoY + gap * 2);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BLACK);
  const addrLines = doc.splitTextToSize(estimateData.Address || '', 100);
  doc.text(': ' + addrLines[0], lv, infoY + gap * 2);
  if (addrLines.length > 1) doc.text(addrLines.slice(1), lv + 2, infoY + gap * 2 + 4);

  // ── Middle column ──
  const mc = lm + 143;
  const mv = lm + 160;
  infoRow(doc, mc, mv, infoY,        'Cost',    fmt(estimateData.TotalAmount));
  infoRow(doc, mc, mv, infoY + gap,  'Advance', fmt(estimateData.AdvanceAmount));
  infoRow(doc, mc, mv, infoY + gap * 2, 'Balance', fmt(estimateData.BalanceAmount));

  // ── Right column ──
  const rc = lm + 223;
  const rv = lm + 244;
  infoRow(doc, rc, rv, infoY,        'Order No',   buildOrderNo(estimateData, companyData.BillPrefix));
  infoRow(doc, rc, rv, infoY + gap,  'Order Date', fmtDate(estimateData.EstimateDate));
  infoRow(doc, rc, rv, infoY + gap * 2, 'Due Date', fmtDate(estimateData.OrderDate));

  // ══ DETAILS TABLE ════════════════════════════════════════════════════════
  const tableStartY = boxY + boxH + 4;
  const details     = estimateData.EstimateDetails || [];

  const rows = details.map((d, i) => [
    i + 1,
    sizeLabel(d),
    getName(d, 'Frame', 'FrameId'),
    getName(d, 'Glass', 'GlassId'),
    getName(d, 'Lamination', 'LaminationId'),
    getName(d, 'Mount', 'MountId'),
    d.MountWidth ? String(d.MountWidth) : 'NIL',
    getName(d, 'Board', 'BoardId'),
    d.HookCount > 0 ? String(d.HookCount) : 'NIL',
    getName(d, 'Stand', 'StandId'),
    getPrintName(d),
    parseFloat(d.Rate || 0).toFixed(2),
    String(d.Quantity || 1),
    parseFloat(d.Amount || 0).toFixed(2),
  ]);

  // Pad to at least 8 rows
  while (rows.length < 8) rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '']);

  autoTable(doc, {
    startY: tableStartY,
    head: [['SN', 'WXH(INCH)', 'Frames', 'Glass', 'Lamn', 'Mount', 'MNT-WIDTH', 'Board', 'Hook', 'Stand', 'Print', 'Rate', 'Qty', 'Amount']],
    body: rows,
    theme: 'grid',
    tableWidth: pw - lm * 2,   // stretch to full printable width
    styles: {
      fontSize: 7.5,
      cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
      lineColor: GOLD,
      lineWidth: 0.15,
      textColor: [30, 30, 30],
      overflow: 'linebreak',
      font: 'helvetica',
    },
    headStyles: {
      fillColor: false,
      textColor: BLACK,
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 7.5,
      lineWidth: 0.2,
      lineColor: GOLD,
      minCellHeight: 7,
    },
    alternateRowStyles: {
      fillColor: LIGHT_GOLD,
    },
    columnStyles: {
      0:  { halign: 'center', cellWidth: 9  },   // SN
      1:  { halign: 'center', cellWidth: 21 },   // WXH
      2:  { cellWidth: 47 },                      // Frames
      3:  { cellWidth: 17 },                      // Glass
      4:  { cellWidth: 17 },                      // Lamn
      5:  { cellWidth: 19 },                      // Mount
      6:  { halign: 'center', cellWidth: 23 },   // MNT-WIDTH
      7:  { cellWidth: 22 },                      // Board
      8:  { halign: 'center', cellWidth: 13 },   // Hook
      9:  { cellWidth: 20 },                      // Stand
      10: { cellWidth: 27 },                      // Print
      11: { halign: 'right',  cellWidth: 19 },   // Rate
      12: { halign: 'center', cellWidth: 14 },   // Qty
      13: { halign: 'right',  cellWidth: 18 },   // Amount
    },
    margin: { left: lm, right: lm },
  });

  // ══ TOTALS SECTION ═══════════════════════════════════════════════════════
  const afterTable = (doc.lastAutoTable?.finalY ?? tableStartY + 60) + 3;

  const totBoxW = 70;
  const totBoxX = rm - totBoxW;

  // Totals background box
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.roundedRect(totBoxX, afterTable, totBoxW, 26, 2, 2, 'S');

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BLACK);

  const totRows = [
    ['Amount',    fmt(estimateData.Amount)],
    ['Others',    fmt(estimateData.OtherAmount)],
    ['Round Off', fmt(estimateData.Roundoff)],
  ];

  let ty = afterTable + 6;
  totRows.forEach(([label, value]) => {
    doc.text(label, rm - 22, ty, { align: 'right' });
    doc.text(value, rm - 2, ty, { align: 'right' });
    ty += 5;
  });

  // Total — highlighted row
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.roundedRect(totBoxX, ty - 4, totBoxW, 7, 1, 1, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLACK);
  doc.text('Total', rm - 22, ty + 0.5, { align: 'right' });
  doc.text(fmt(estimateData.TotalAmount), rm - 2, ty + 0.5, { align: 'right' });

  // ══ SIGNATURE AREA ════════════════════════════════════════════════════════
  ty += 12;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BLACK);

  // ══ FOOTER ════════════════════════════════════════════════════════════════
  const fy = ph - 14;

  // Clamp notes/signature area so it never overlaps the footer
  if (ty > fy - 14) ty = fy - 14;

  const notesText = estimateData.Remarks || buildNotesFromDetails(details);

  // ── Notes block (above signature line) ──
  if (notesText) {
    const notesLines = doc.splitTextToSize(notesText, pw - lm * 2 - 60);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes : ', lm, ty);
    doc.setFont('helvetica', 'normal');
    doc.text(notesLines, lm + 16, ty);
    ty += Math.max(0, (notesLines.length - 1) * 4.5);
  }

  // ── For Company Name + Authorised Signatory — pinned just above the footer line ──
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK_BROWN);
  doc.setFontSize(10);
  doc.text(`For ${companyName}`, rm, fy - 18, { align: 'right' });
  doc.setFontSize(8.5);
  doc.setTextColor(...BLACK);
  doc.text('Authorised  Signatory', rm, fy - 6, { align: 'right' });

  // Gold top line
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(lm, fy - 4, rm, fy - 4);

  // Bottom bar
  doc.setFillColor(245, 240, 230);
  doc.rect(0, ph - 8, pw, 8, 'F');

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 40, 10);

  // Footer: 4 equal columns across printable width
  const colW   = (rm - lm) / 4;
  const col1   = lm;
  const col3   = lm + colW * 2;
  const col4   = lm + colW * 3;

  // Address — 2 lines
  doc.text(`${companyAddr1} ${companyAddr2}`, col1, fy);
  doc.text(companyCity,                       col1, fy + 5);

  // Phones — 2 lines
  doc.text(`Tel : ${companyPhone1}  Mob : ${companyPhone2}`, col3, fy);
  doc.text(companyPhone3,                                     col3, fy + 5);

  doc.setTextColor(...BLUE_LINK);
  doc.text(companyEmail, col4, fy);
  doc.text(companyWeb,   col4, fy + 5);
  doc.setTextColor(0, 0, 0);

  // Text in bottom bar
  doc.setTextColor(...BLACK);
  doc.setFontSize(7);
  doc.text(`© ${companyName} — Quality Framing Since 2000`, pw / 2, ph - 3.5, { align: 'center' });

  return doc;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Open PDF in browser print dialog */
export const printEstimatePDF = (estimateData, companyData = {}) => {
  const doc = buildDoc(estimateData, companyData);
  const blobUrl = doc.output('bloburl');
  const win = window.open(blobUrl, '_blank');
  if (win) {
    win.addEventListener('load', () => {
      win.focus();
      win.print();
    });
  }
};

/** Download PDF as file */
export const downloadEstimatePDF = (estimateData, companyData = {}) => {
  const doc = buildDoc(estimateData, companyData);
  const fileName = `WorkOrder_${buildOrderNo(estimateData, companyData.BillPrefix)}_${(estimateData.ClientName || 'Estimate').replace(/\s+/g, '_')}.pdf`;
  doc.save(fileName);
};

/** Share via WhatsApp */
export const shareEstimateWhatsApp = async (estimateData, companyData = {}) => {
  const doc   = buildDoc(estimateData, companyData);
  const name  = estimateData.ClientName || 'Customer';
  const order = buildOrderNo(estimateData, companyData.BillPrefix);
  const total = fmt(estimateData.TotalAmount);
  const bal   = fmt(estimateData.BalanceAmount);
  const date  = fmtDate(estimateData.EstimateDate);
  const cName = companyData.Name || 'Cochin Frames';
  const cPhone = companyData.ContactNumber2 || companyData.ContactNumber1 || '7356864227';
  const cWeb   = companyData.Website || 'www.cochinframes.com';

  const message =
    `🖼️ *${cName.toUpperCase()} — Work Order*\n\n` +
    `📋 Order No : *${order}*\n` +
    `👤 Name     : *${name}*\n` +
    `📅 Date     : ${date}\n` +
    `💰 Total    : ₹${total}\n` +
    `💳 Balance  : ₹${bal}\n\n` +
    `_Thank you for choosing ${cName}!_\n` +
    `📞 ${cPhone} | 🌐 ${cWeb}`;

  // Try Web Share API with PDF file (works on mobile)
  if (navigator.canShare) {
    try {
      const pdfBlob = doc.output('blob');
      const file    = new File([pdfBlob], `WorkOrder_${order}.pdf`, { type: 'application/pdf' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Work Order ${order}`, text: message });
        return;
      }
    } catch (_) { /* fall through */ }
  }

  // Fallback — open WhatsApp web with text message
  const phone = (estimateData.ContactNumber || '').replace(/\D/g, '');
  const url   = phone
    ? `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank');
};

// Keep backward compat
export const generateEstimatePDF = printEstimatePDF;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function infoRow(doc, labelX, valueX, y, label, value) {
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text(label, labelX, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(': ' + value, valueX, y);
}

function fmt(val) {
  return parseFloat(val || 0).toFixed(2);
}

function fmtDate(dateStr) {
  if (!dateStr) return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const d   = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const mon = d.toLocaleString('en-GB', { month: 'short' });
  const yr  = d.getFullYear();
  return `${day}/${mon}/${yr}`;
}

function buildOrderNo(estimate, billPrefix) {
  const prefix = billPrefix || 'PDVM';
  return `${prefix}${estimate.OrderNumber || 0}`;
}

function sizeLabel(detail) {
  const w = detail.Width || '';
  const h = detail.Height || '';
  if (w && h) return `${w}X${h}`;
  return detail.Size?.Name || '';
}

function getName(detail, objKey, idKey) {
  if (detail[objKey]?.Name) return detail[objKey].Name;
  if (detail[idKey]) return String(detail[idKey]);
  return 'NIL';
}

function getPrintName(detail) {
  if (detail.Print?.PrintType) return detail.Print.PrintType;
  if (detail.Print?.Name) return detail.Print.Name;
  if (detail.PrintId) return String(detail.PrintId);
  return 'NIL';
}

function buildNotesFromDetails(details) {
  if (!details?.length) return '';
  return details.map(d => sizeLabel(d)).filter(Boolean).join(', ');
}
